import { Component } from "react";
import { AreaChart, Badge, Box, Button, Container, DateRangePicker, Grid, Modal, Multiselect, SpaceBetween } from "@cloudscape-design/components";
import { analyticsBannerInfoModalStore } from "../stores/analyticsBanners";
import { useRecoilState, useRecoilValue } from "recoil";
import { userStore } from "../stores/access";

const formatCtr = (value) => {
	const numericValue = Number(value);

	return `${Number.isFinite(numericValue) ? numericValue.toFixed(2) : "0.00"}%`;
};

const getDateKey = (value) => {
	if (value instanceof Date) return value.toISOString().split("T")[0];

	return String(value).split("T")[0];
};

export class BannersAnalyticsInfoModalBase extends Component {
	static defaultProps = {
		showPositionSeries: true,
	};

	constructor(props) {
		super(props);

		this.state = {
			products: [],
			date: {
				type: "absolute",
				startDate: false,
				endDate: false,
			},
		};

		this.productsDebounceTimer = null;
	}

	getBannerInfo(options) {
		const bannerName = this.props.bannerInfo.currentBanner;
		const products = options?.products || this.state.products;
		const date = {
			from: options?.date?.startDate || this.state.date.startDate,
			to: options?.date?.endDate || this.state.date.endDate,
		};

		if (this.props.loadAnalyticsInfo) {
			this.props.loadAnalyticsInfo(bannerName, date, products);
		} else {
			this.props.Interface.loadBannerAnalyticsInfo(bannerName, date, products);
		}
	}

	componentDidMount() {
		this.getBannerInfo();
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.date !== this.state.date) {
			this.getBannerInfo({ date: this.state.date });
		}

		if (prevState.products !== this.state.products) {
			clearTimeout(this.productsDebounceTimer);

			this.productsDebounceTimer = setTimeout(() => {
				this.getBannerInfo({
					products: this.state.products.map((item) => item.value),
				});
			}, 500);
		}
	}

	componentWillUnmount() {
		this.props.setBannerInfo((prev) => ({
			...prev,
			loaded: false,
			currentBanner: "",
			data: [],
			positionSeries: [],
			totalClick: 0,
			totalView: 0,
			totalCtr: 0,
			error: {
				text: "",
				code: 0,
				timestamp: 0,
			},
		}));
	}

	getChartSeries() {
		const data = this.props.bannerInfo.data || [];

		if (data.length === 0) return [];

		const series = [
			{
				title: "Clicks",
				type: "area",
				data: data.map((item) => ({ x: new Date(item.date), y: item.click })),
			},
			{
				title: "Views",
				type: "area",
				data: data.map((item) => ({ x: new Date(item.date), y: item.view })),
			},
			{
				title: "CTR",
				type: "area",
				data: data.map((item) => ({ x: new Date(item.date), y: Number.isFinite(Number(item.ctr)) ? Number(item.ctr) : 0 })),
				valueFormatter: function l(e) {
					return formatCtr(e);
				},
			},
		];

		if (this.props.showPositionSeries === false) return series;

		return series.concat(
			(this.props.bannerInfo.positionSeries || [])
				.filter((item) => item.data.length > 0)
				.map((item) => {
					const positionsByDate = new Map(
						item.data.map((point) => {
							const position = Number(point.position);

							return [getDateKey(point.date), Number.isFinite(position) && position > 0 ? position : 0];
						}),
					);

					return {
						title: item.product ? `Position (${item.product})` : "Position",
						type: "area",
						// AreaChart matches series by array index, so every series must use the same dates.
						data: data.map((point) => ({ x: new Date(point.date), y: positionsByDate.get(getDateKey(point.date)) || 0 })),
						valueFormatter: function l(e) {
							const numericValue = Number(e);

							return Number.isFinite(numericValue) && numericValue > 0 ? String(numericValue) : "—";
						},
					};
				}),
		);
	}

	render() {
		return (
			<Modal visible={this.props.bannerInfo.show} onDismiss={() => this.props.setBannerInfo((prev) => ({ ...prev, show: false }))} size="large" header={`Statistics: ${this.props.bannerInfo.currentBanner}`}>
				<Box margin={{ bottom: "s" }}>
					<SpaceBetween direction="horizontal" size="s">
						<DateRangePicker
							onChange={({ detail }) => {
								this.setState({
									date: detail.value || {
										type: "absolute",
										startDate: false,
										endDate: false,
									},
								});
							}}
							value={this.state.date}
							isValidRange={(range) => {
								if (range.type === "absolute") {
									const [startDateWithoutTime] = range.startDate.split("T");
									const [endDateWithoutTime] = range.endDate.split("T");
									if (!startDateWithoutTime || !endDateWithoutTime) {
										return {
											valid: false,
											errorMessage: "The selected date range is incomplete. Select a start and end date for the date range.",
										};
									}
									if (new Date(range.startDate) - new Date(range.endDate) > 0) {
										return {
											valid: false,
											errorMessage: "The selected date range is invalid. The start date must be before the end date.",
										};
									}
								} else {
									return { valid: false, errorMessage: "Only Absolute mode" };
								}
								return { valid: true };
							}}
							i18nStrings={{
								todayAriaLabel: "Today",
								nextMonthAriaLabel: "Next month",
								previousMonthAriaLabel: "Previous month",
								customRelativeRangeDurationLabel: "Duration",
								customRelativeRangeDurationPlaceholder: "Enter duration",
								customRelativeRangeOptionLabel: "Custom range",
								customRelativeRangeOptionDescription: "Define a custom range in the past",
								customRelativeRangeUnitLabel: "Unit of time",
								formatRelativeRange: ({ startDate, endDate }) => {
									const start = new Date(startDate);
									const end = new Date(endDate);

									const locale = "en-GB";
									const dateOptions = { year: "numeric", month: "long", day: "numeric" };
									const timeOptions = { hour: "2-digit", minute: "2-digit", second: "2-digit" };

									const formattedStart = start.toLocaleDateString(locale, dateOptions) + " " + start.toLocaleTimeString(locale, timeOptions);
									const formattedEnd = end.toLocaleDateString(locale, dateOptions) + " " + end.toLocaleTimeString(locale, timeOptions);

									return `${formattedStart} - ${formattedEnd}`;
								},
								formatUnit: (unit, amount) => `${unit}${amount > 1 ? "s" : ""}`,
								dateTimeConstraintText: "Date and time must be within the selected range.",
								renderSelectedAbsoluteRangeAriaLive: (startDate, endDate) => `Selected from ${startDate} to ${endDate}`,
								labelRelativeRange: "Relative range",
								labelAbsoluteRange: "Absolute range",
								dateTimePickerAriaLabel: "Date and time picker",
								dateTimePickerLabel: "Date and Time",
								clearButtonAriaLabel: "Clear date range",
								relativeModeTitle: "Relative mode",
								absoluteModeTitle: "Absolute mode",
								cancelButtonLabel: "Cancel",
								applyButtonLabel: "Apply",
								clearButtonLabel: "Clear and dismiss",
							}}
							dateOnly
							expandToViewport
							hideTimeOffset
							disabled={!this.props.bannerInfo.loaded}
							placeholder="from/to"
							showClearButton={true}
						/>
						<Multiselect
							selectedOptions={this.state.products}
							placeholder="Chose products"
							hideTokens
							disabled={!this.props.bannerInfo.loaded || this.props.brandList.length === 0}
							onChange={({ detail }) => {
								this.setState({ products: detail.selectedOptions });
							}}
							options={this.props.brandList.map((item) => ({ value: item.id, label: item.name }))}
						/>
					</SpaceBetween>
				</Box>
				<SpaceBetween size="l">
					<Container>
						<AreaChart
							statusType={this.props.bannerInfo.loaded ? "finished" : "loading"}
							series={this.getChartSeries()}
							xDomain={this.props.bannerInfo.xDomain}
							i18nStrings={{
								filterPlaceholder: "Chose column ",
								detailTotalFormatter: () => "",
								xTickFormatter: (e) =>
									e
										.toLocaleDateString("en-US", {
											month: "short",
											day: "numeric",
										})
										.split(",")
										.join("\n"),
								yTickFormatter: function o(e) {
									return Math.abs(e) >= 1e9 ? (e / 1e9).toFixed(1).replace(/\.0$/, "") + "G" : Math.abs(e) >= 1e6 ? (e / 1e6).toFixed(1).replace(/\.0$/, "") + "M" : Math.abs(e) >= 1e3 ? (e / 1e3).toFixed(1).replace(/\.0$/, "") + "K" : e.toFixed(2);
								},
							}}
							height={300}
							xScaleType="time"
							ariaLabel="Stacked area chart, multiple metrics"
							yTitle=""
							empty={
								<Box textAlign="center" color="inherit">
									<b>No data available</b>
									<Box variant="p" color="inherit">
										Try adjusting your filters
									</Box>
								</Box>
							}
							noMatch={
								<Box textAlign="center" color="inherit">
									<b>No matching data</b>
									<Box variant="p" color="inherit">
										Try adjusting your filters
									</Box>
								</Box>
							}
						/>
					</Container>
					<Container>
						<Grid gridDefinition={[{ colspan: 4 }, { colspan: 4 }, { colspan: 4 }]}>
							<div style={{ textAlign: "center", padding: "5px", background: "rgb(50 66 82 / 10%)", borderRadius: "8px" }}>
								<Box variant="h1" color="text-status-info">
									{this.props.bannerInfo.totalClick}
								</Box>
								<Box variant="small" color="text-label">
									Total Clicks
								</Box>
							</div>

							<div style={{ textAlign: "center", padding: "5px", background: "rgb(50 66 82 / 10%)", borderRadius: "8px" }}>
								<Box variant="h1" color="text-status-error">
									{this.props.bannerInfo.totalView}
								</Box>
								<Box variant="small" color="text-label">
									Total Views
								</Box>
							</div>

							<div style={{ textAlign: "center", padding: "5px", background: "rgb(50 66 82 / 10%)", borderRadius: "8px" }}>
								<Box variant="h1" color="text-status-success">
									{formatCtr(this.props.bannerInfo.totalCtr)}
								</Box>
								<Box variant="small" color="text-label">
									Total CTR
								</Box>
							</div>
						</Grid>
					</Container>
				</SpaceBetween>
			</Modal>
		);
	}
}

function withRecoil(Component) {
	return function WrappedComponent(props) {
		const [bannerInfo, setBannerInfo] = useRecoilState(analyticsBannerInfoModalStore);
		const user = useRecoilValue(userStore);
		const brandList = user.brandsList || [];

		return <Component {...props} bannerInfo={bannerInfo} brandList={brandList} setBannerInfo={setBannerInfo} />;
	};
}

export default withRecoil(BannersAnalyticsInfoModalBase);
